import { useEffect, useRef, useState } from 'react';
import { elementNodesInScene } from '../utils/vectorPath';
import { shapePathD } from '../schema/shapeLibrary';

const FIGMA_BLUE = '#0d99ff';

/** Mirror a handle across its node — what keeps a smooth node smooth. */
function mirror(node, h) {
    return [2 * node.x - h[0], 2 * node.y - h[1]];
}

/** The point on a cubic segment at t, used to place an inserted node. */
function cubicAt(a, b, t) {
    const p0 = [a.x, a.y];
    const p1 = a.hOut || [a.x, a.y];
    const p2 = b.hIn || [b.x, b.y];
    const p3 = [b.x, b.y];
    const u = 1 - t;
    const at = (i) => (u * u * u) * p0[i] + 3 * (u * u) * t * p1[i] + 3 * u * (t * t) * p2[i] + (t * t * t) * p3[i];
    return { x: at(0), y: at(1) };
}

/** Distance from p to the segment a→b, flattened — close enough for hit-testing. */
function segmentHit(p, a, b) {
    let best = { d: Infinity, t: 0 };
    const STEPS = 24;
    for (let i = 0; i <= STEPS; i += 1) {
        const t = i / STEPS;
        const q = cubicAt(a, b, t);
        const d = Math.hypot(q.x - p.x, q.y - p.y);
        if (d < best.d) best = { d, t };
    }
    return best;
}

/**
 * Anchor editing for a drawn or flattened path — Illustrator's white arrow,
 * Figma's double-click-into-a-vector.
 *
 * Everything here works in SCENE coordinates: the store converts back to the
 * unit box and re-fits the element's box on commit, so an anchor dragged past
 * the old edge simply takes the box with it.
 *
 *  - drag an anchor to move it, ⇧ to keep it on one axis
 *  - drag a handle to bend the curve; ⌥ breaks the pair so the sides move alone
 *  - ⌥-click an anchor to switch it between corner and smooth
 *  - double-click a segment to insert an anchor, ⌫ to delete the selected one
 *  - Enter or Esc leaves node editing
 */
export default function PathEditOverlay({
    element,
    px,
    clientToCanvas,
    onChange,
    onExit,
}) {
    const [nodes, setNodes] = useState(() => elementNodesInScene(element));
    const [active, setActive] = useState(0);
    const dragRef = useRef(null);
    // The live nodes, readable from the window listeners without re-subscribing.
    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;
    const closed = !!element.path?.closed;

    // Follow the element while something ELSE moves it (a nudge, the panel, undo).
    // A drag of our own is the authority until it lets go.
    useEffect(() => {
        if (dragRef.current) return;
        setNodes(elementNodesInScene(element));
    }, [element]);

    const commit = (list, { done = false } = {}) => {
        setNodes(list);
        onChange(list, closed, { done });
    };

    useEffect(() => {
        const move = (e) => {
            const drag = dragRef.current;
            if (!drag) return;
            const p = clientToCanvas(e.clientX, e.clientY);
            const list = nodesRef.current.map((n) => ({ ...n }));
            const n = list[drag.index];
            if (!n) return;

            if (drag.kind === 'node') {
                let { x, y } = p;
                if (e.shiftKey) {
                    // ⇧ keeps the anchor on the axis it has travelled furthest along.
                    if (Math.abs(p.x - drag.from.x) >= Math.abs(p.y - drag.from.y)) y = drag.from.y;
                    else x = drag.from.x;
                }
                const dx = x - n.x;
                const dy = y - n.y;
                n.x = x;
                n.y = y;
                // Handles belong to the anchor: they travel with it.
                if (n.hIn) n.hIn = [n.hIn[0] + dx, n.hIn[1] + dy];
                if (n.hOut) n.hOut = [n.hOut[0] + dx, n.hOut[1] + dy];
            } else {
                const side = drag.kind === 'hIn' ? 'hIn' : 'hOut';
                const other = side === 'hIn' ? 'hOut' : 'hIn';
                n[side] = [p.x, p.y];
                // A smooth anchor keeps its two sides in line; ⌥ breaks the pair so
                // a corner can be shaped one side at a time.
                if (!e.altKey && n[other]) n[other] = mirror(n, n[side]);
            }
            list[drag.index] = n;
            commit(list);
        };
        const up = () => {
            if (!dragRef.current) return;
            dragRef.current = null;
            commit(nodesRef.current, { done: true });
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        return () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientToCanvas, closed]);

    // Keyboard: leave the mode, or delete the anchor being worked on.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' || e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                onExit();
                return;
            }
            if (e.key !== 'Backspace' && e.key !== 'Delete') return;
            const list = nodesRef.current;
            // Two anchors are the least a path can be; below that, deleting one
            // would leave nothing to draw.
            if (list.length <= 2) return;
            e.preventDefault();
            e.stopPropagation();
            const next = list.filter((_, i) => i !== active);
            setActive(Math.max(0, active - 1));
            commit(next, { done: true });
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, onExit, closed]);

    const startDrag = (e, kind, index) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        setActive(index);
        // ⌥-click on an anchor switches it between corner and smooth instead of
        // dragging: the fastest way to straighten a curve or round a corner.
        if (kind === 'node' && e.altKey) {
            const list = nodesRef.current.map((n) => ({ ...n }));
            const n = list[index];
            if (n.hIn || n.hOut) {
                delete n.hIn;
                delete n.hOut;
            } else {
                const prev = list[(index - 1 + list.length) % list.length];
                const next = list[(index + 1) % list.length];
                // A third of the way towards each neighbour is the handle length
                // that turns a corner into a curve without changing the outline's
                // path through the anchor.
                n.hOut = [n.x + (next.x - prev.x) / 6, n.y + (next.y - prev.y) / 6];
                n.hIn = mirror(n, n.hOut);
            }
            list[index] = n;
            commit(list, { done: true });
            return;
        }
        dragRef.current = { kind, index, from: { x: nodesRef.current[index].x, y: nodesRef.current[index].y } };
    };

    /** Double-click on the outline inserts an anchor where it was clicked. */
    const onOutlineDoubleClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const p = clientToCanvas(e.clientX, e.clientY);
        const list = nodesRef.current;
        const pairs = [];
        for (let i = 1; i < list.length; i += 1) pairs.push([i - 1, i]);
        if (closed && list.length > 2) pairs.push([list.length - 1, 0]);
        let best = null;
        pairs.forEach(([ai, bi]) => {
            const hit = segmentHit(p, list[ai], list[bi]);
            if (!best || hit.d < best.d) best = { ...hit, ai, bi };
        });
        // A screen-constant 12px of slack, whatever the zoom.
        if (!best || best.d > px(12)) return;
        const at = cubicAt(list[best.ai], list[best.bi], best.t);
        const next = [...list];
        next.splice(best.bi === 0 ? list.length : best.bi, 0, { x: at.x, y: at.y });
        setActive(best.bi === 0 ? list.length : best.bi);
        commit(next, { done: true });
    };

    const local = (p) => ({ x: p.x - element.x, y: p.y - element.y });
    const d = shapePathD(element, Math.max(1, element.width), Math.max(1, element.height));
    const anchorSize = px(8);
    const handleSize = px(6);

    return (
        <div
            data-export-ignore="1"
            className="absolute pointer-events-none"
            style={{
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                zIndex: 10000,
            }}
        >
            {/* The outline itself, drawn over the artwork so the shape being edited
                reads clearly, and doubling as the target for inserting anchors. */}
            <svg
                className="absolute overflow-visible"
                style={{ left: 0, top: 0, width: element.width, height: element.height, pointerEvents: 'none' }}
            >
                <path d={d} fill="none" stroke={FIGMA_BLUE} strokeWidth={px(1)} />
                <path
                    d={d}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={px(10)}
                    style={{ pointerEvents: 'stroke' }}
                    onDoubleClick={onOutlineDoubleClick}
                />
                {/* Handle arms — only for the anchor being worked on, as Figma does:
                    every arm at once turns a curve into a thicket. */}
                {nodes.map((n, i) => (i === active ? (
                    <g key={`arm-${i}`}>
                        {n.hIn && (
                            <line
                                x1={local(n).x} y1={local(n).y}
                                x2={n.hIn[0] - element.x} y2={n.hIn[1] - element.y}
                                stroke={FIGMA_BLUE} strokeWidth={px(1)}
                            />
                        )}
                        {n.hOut && (
                            <line
                                x1={local(n).x} y1={local(n).y}
                                x2={n.hOut[0] - element.x} y2={n.hOut[1] - element.y}
                                stroke={FIGMA_BLUE} strokeWidth={px(1)}
                            />
                        )}
                    </g>
                ) : null))}
            </svg>

            {nodes.map((n, i) => {
                const p = local(n);
                const on = i === active;
                return (
                    <div
                        key={`node-${i}`}
                        title="Drag to move · ⌥-click for corner/curve · ⌫ to delete"
                        className="absolute pointer-events-auto cursor-move"
                        style={{
                            width: anchorSize,
                            height: anchorSize,
                            left: p.x - anchorSize / 2,
                            top: p.y - anchorSize / 2,
                            backgroundColor: on ? FIGMA_BLUE : '#ffffff',
                            border: `${px(1.25)}px solid ${FIGMA_BLUE}`,
                            borderRadius: px(1.5),
                            boxShadow: `0 0 ${px(2)}px rgba(0,0,0,0.25)`,
                        }}
                        onPointerDown={(e) => startDrag(e, 'node', i)}
                    />
                );
            })}

            {/* Round handle dots, on the active anchor only. */}
            {nodes.map((n, i) => (i === active ? ['hIn', 'hOut'].map((side) => {
                const h = n[side];
                if (!h) return null;
                return (
                    <div
                        key={`${side}-${i}`}
                        title="Drag to bend the curve · ⌥ to move this side alone"
                        className="absolute pointer-events-auto cursor-move bg-white"
                        style={{
                            width: handleSize,
                            height: handleSize,
                            left: h[0] - element.x - handleSize / 2,
                            top: h[1] - element.y - handleSize / 2,
                            border: `${px(1.25)}px solid ${FIGMA_BLUE}`,
                            borderRadius: '50%',
                        }}
                        onPointerDown={(e) => startDrag(e, side, i)}
                    />
                );
            }) : null))}
        </div>
    );
}
